import styled, { keyframes, css } from "styled-components";
import logoLapiezaWhite from "../../assets/logo-lapieza-white.png";
import heroHeadhunting from "../../assets/hero/headhunting.png";
import { palette, fonts, radii } from "../../styles/palette";

export { css };

export const pulse = keyframes`
  0% { box-shadow: 0 0 0 0 ${palette.redGlow}; }
  70% { box-shadow: 0 0 0 16px ${palette.redGlowNone}; }
  100% { box-shadow: 0 0 0 0 ${palette.redGlow}; }
`;

export const blink = keyframes`
  0% { opacity: 1; }
  12.5% { opacity: 0.3; }
  25% { opacity: 1; }
  37.5% { opacity: 0.3; }
  50% { opacity: 1; }
  100% { opacity: 1; }
`;

export const RefreshButton = styled.button<{
  $isBlinking: boolean;
  $isPlaying: boolean;
}>`
  align-self: center;
  background: transparent;
  border: none;
  color: ${palette.navy};
  font-size: 16px;
  cursor: ${({ $isPlaying }) => ($isPlaying ? "not-allowed" : "pointer")};
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  border-radius: ${radii.pill};
  transition: all 0.2s ease;
  opacity: ${({ $isPlaying }) => ($isPlaying ? 0.5 : 1)};
  text-decoration: underline;
  text-decoration-color: ${palette.red};
  text-underline-offset: 3px;
  font-weight: 600;
  animation: ${({ $isBlinking }) =>
    $isBlinking
      ? css`
          ${blink} 2s ease-in-out
        `
      : "none"};

  &:hover {
    background: ${palette.surfaceMuted};
    color: ${palette.red};
    text-decoration: none;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

export const Overlay = styled.div`
  position: fixed;
  inset: 0;
  z-index: 2000;
  background: ${palette.navy};
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  min-width: 100vw;
  overflow: hidden;

  /* Marca de agua de marca (hero LaPieza). */
  &::after {
    content: "";
    position: absolute;
    right: -70px;
    bottom: -70px;
    width: min(45vw, 380px);
    height: min(45vw, 380px);
    background: url(${heroHeadhunting}) no-repeat center / contain;
    opacity: 0.22;
    pointer-events: none;
    z-index: 0;
  }

  &::before {
    content: "";
    position: absolute;
    top: 24px;
    left: 24px;
    width: 150px;
    height: 34px;
    background: url(${logoLapiezaWhite}) no-repeat left center / contain;
    pointer-events: none;
    z-index: 5000;
  }
`;

export const Card = styled.div`
  background: ${palette.white};
  border-radius: ${radii.md};
  box-shadow: 0 18px 48px rgba(35, 35, 35, 0.24);
  width: 100%;
  max-width: 420px;
  min-height: 480px;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 50px 20px 0 20px;
  position: relative;
  z-index: 1;
  border: 1px solid ${palette.border};
`;

export const Avatar = styled.div.withConfig({
  shouldForwardProp: (prop) => prop !== "isSpeaking",
})<{ isSpeaking: boolean }>`
  width: 100px;
  height: 100px;
  border-radius: 50%;
  /* Fondo siempre claro: el logotipo debe seguir legible al hablar; el estado
     "hablando" se comunica con el borde y el anillo rojos. */
  background: ${palette.white};
  border: 1.5px solid
    ${({ isSpeaking }) => (isSpeaking ? palette.red : palette.border)};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 44px;
  color: ${palette.navy};
  margin-bottom: 12px;
  box-shadow: ${({ isSpeaking }) =>
    isSpeaking ? `0 0 0 8px ${palette.redGlowSoft}` : "none"};
  transition: border-color 0.3s, box-shadow 0.3s;
  ${({ isSpeaking }) =>
    isSpeaking &&
    css`
      animation: ${pulse} 1s infinite;
    `}
  overflow: hidden;
`;

export const Greeting = styled.div`
  font-family: ${fonts.heading};
  font-size: 22px;
  font-weight: 600;
  color: ${palette.navy};
  margin-bottom: 16px;
  text-align: center;
`;

export const StatusTitle = styled.div`
  font-family: ${fonts.heading};
  font-weight: 600;
  font-size: 18px;
  color: ${palette.navy};
  margin-bottom: 2px;
`;

export const StatusSub = styled.div`
  font-size: 14px;
  color: ${palette.textSecondary};
  margin-bottom: 18px;
`;

export const ButtonRow = styled.div`
  display: flex;
  gap: 16px;
  width: 100%;
  justify-content: center;
  align-items: center;

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 12px;
  }
`;

export const CallButton = styled.button`
  background: ${palette.red} !important;
  color: ${palette.white} !important;
  border: none !important;
  border-radius: 50%;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 14px ${palette.redGlow};
  animation: ${pulse} 2s infinite;

  &:hover {
    background: ${palette.redHover} !important;
    transform: translateY(-2px);
    box-shadow: 0 6px 20px ${palette.redGlow};
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 4px 12px ${palette.shadowMedium};
  }

  &:disabled {
    background: ${palette.gray} !important;
    color: ${palette.textSecondary} !important;
    opacity: 1;
    cursor: not-allowed;
    animation: none;
    transform: none;
    box-shadow: none;
  }

  @media (max-width: 480px) {
    width: 70px;
    height: 70px;
  }
`;

export const HangupButton = styled.button`
  background: ${palette.navy};
  color: ${palette.white};
  border: none;
  border-radius: 50%;
  width: 80px;
  height: 80px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px ${palette.shadowMedium};

  &:hover {
    background: ${palette.navyDark};
    transform: translateY(-2px);
    box-shadow: 0 6px 20px ${palette.shadowDark};
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 4px 12px ${palette.shadowMedium};
  }

  @media (max-width: 480px) {
    width: 70px;
    height: 70px;
  }
`;

export const CallTimer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin: 16px 0 24px 0;
  gap: 12px;
`;

export const TimerDisplay = styled.div`
  font-family: ${fonts.heading};
  font-size: 24px;
  font-weight: 600;
  color: ${palette.navy};
  text-align: center;
  font-variant-numeric: tabular-nums;
`;

export const ProgressBarContainer = styled.div`
  width: 100%;
  max-width: 300px;
  height: 8px;
  background: ${palette.gray};
  border-radius: ${radii.pill};
  overflow: hidden;
`;

export const ProgressBar = styled.div<{ progress: number }>`
  height: 100%;
  background: ${palette.red};
  width: ${({ progress }) => progress}%;
  transition: width 0.3s ease;
`;

export const CallStatusMessage = styled.p`
  color: ${palette.red};
  font-weight: 500;
  text-align: center;
  margin-bottom: 18px;
  line-height: 1.6;
`;

export const AvatarImage = styled.img`
  width: 74px;
  height: auto;
  object-fit: contain;
`;

export const LoadingSpinner = styled.span`
  font-size: 22px;
`;

export const ErrorText = styled.div`
  padding: 40px;
  color: ${palette.red};
`;
